<template>
    <div
        v-show="widgetDisplayCount > 0 && isShowWidgetSwitch"
        ref="widgetSwitchRef"
        class="widgetSwitch"
        v-bind:class="{
            _oneGame: widgetDisplayCount === 1,
            _showList: isShowList,
        }"
        v-bind:style="{
            zIndex: zIndexMapping[ZIndex.Widget],
            bottom: isShowJackpotWidget ? '150px' : undefined,
        }"
    >
        <div
            v-if="isMaskEnabled"
            id="widgetMask"
            class="widgetMask"
            v-on:click="isShowMask(false)"
        />
        <div
            class="widgetSwitch-open-button"
            v-bind:class="{ 'centered-content': isShowInjoiDefaultWidgetEnabled, [widgetStatusForGa]: true }"
            v-on:click="showWidgetList($event)"
        >
            <div v-show="!isShowList">
                <div v-if="isShowInjoiDefaultWidgetEnabled">
                    <div
                        id="default-mini-game-widget"
                        v-bind:data-loginName="customerInfo.loginName"
                        v-bind:data-accountId="customerInfo.accountId"
                        v-bind:data-sboCurrency="customerInfo.currency"
                        v-bind:data-isoCurrency="customerInfo.isoCurrency"
                        v-bind:data-license="customerInfo.license"
                        v-bind:data-playerType="PlayerType[customerInfo.playerType]"
                        v-bind:data-brand="brandName"
                        v-bind:data-trackingId="customerInfo.trackingId"
                    /> <!-- to be mounted -->
                </div>
                <picture v-else>
                    <source v-bind:srcset="miniGameImage.webp" type="image/webp">
                    <img
                        v-bind:src="miniGameImage.origin"
                        alt=""
                    >
                </picture>
            </div>
            <span v-show="isShowList">x</span>
        </div>
        <div class="widgetSwitch-item" v-on:click="hideShowList">
            <div
                v-if="isMiniGameWidgetEnabled"
                v-show="widgetDisplayState.miniGame"
                class="JARVIS_Widget_Btn_MiniGame"
                v-on:click="isShowMask(true)"
            >
                <div id="mini-game-widget" /><!-- to be mounted -->
            </div>
            <div
                v-if="isFunkyWidgetEnabled"
                v-show="widgetDisplayState.funky"
                class="JARVIS_Widget_Btn_Funky"
                v-on:click="isShowMask(true)"
            >
                <div id="funky-widget" /><!-- to be mounted -->
            </div>

            <!-- add new widgets here -->
        </div>
    </div>
</template>

<script lang="ts">
    import { onClickOutside } from '@vueuse/core';
    import { storeToRefs } from 'pinia';
    import { computed, defineComponent, onUnmounted, ref, watch } from 'vue';
    import { PlayerType, ZIndex } from '@/interface/enum';
    import { useJackpotDisplay } from '@/libraries/composables/useJackpotDisplay';
    import { getImage } from '@/libraries/helpers/imageHelper';
    import { zIndexMapping } from '@/libraries/helpers/zIndexHelper';
    import { i18n } from '@/libraries/i18n';
    import {
        getFunkyWidgetUrl,
        getMiniGameWidgetUrl,
    } from '@/libraries/url';
    import {
        disableScrolling,
        enableScrolling,
        loadCss,
        loadScript,
    } from '@/libraries/utils/dom';
    import { useAccountStore } from '@/stores/accountStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useHeaderDisplayStore } from '@/stores/headerDisplayStore';
    import { useRootStore } from '@/stores/rootStore';

    export default defineComponent({
        name: 'WidgetSwitch',
        setup() {
            const widgetSwitchRef = ref();

            const { isShowJackpotWidget } = useJackpotDisplay();
            const { isShowInjoiDefaultWidgetEnabled } = storeToRefs(useFeatureToggleStore());

            const isMaskEnabled = ref(false);

            const { isAsi, brandName } = storeToRefs(useRootStore());

            const { isMiniGameWidgetEnabled, inJoiVersion, isFunkyWidgetEnabled, isLoadFunkyWidgetFromGames } = storeToRefs(useFeatureToggleStore());

            const { customerInfo } = storeToRefs(useAccountStore());

            const { isShowWidgetSwitch } = storeToRefs(useHeaderDisplayStore());

            const imageGlob = import.meta.glob('@/assets/images/mini-game/*.{png,webp}', {
                eager: true,
            });

            const miniGameImage = computed(() => getImage(imageGlob, `mini-game_${i18n.locale.value}`));

            const widgetDisplayState = ref<{ funky: boolean | null, miniGame: boolean | null }>({
                funky: null,
                miniGame: null,
                // add new widgets here
            });
            function setWidgetDisplayState({ name, display }: { name: 'funky' | 'miniGame', display: boolean }) {
                widgetDisplayState.value[name] = display;
            }

            const widgetDisplayCount = computed(() => {
                const widgetDisplayStates = Object.values(widgetDisplayState.value);
                if (widgetDisplayStates.includes(null)) return 0;
                return widgetDisplayStates.filter(x => !!x).length;
            });
            const isLoadMiniGameWidget = computed(() => isMiniGameWidgetEnabled.value
                && isAsi.value);

            const isShowList = ref(false);

            function showWidgetList(event: Event) {
                event.stopPropagation(); // prevent triggering click-outside

                isShowList.value = !isShowList.value;
            }

            function hideShowList() {
                isShowList.value = false;
            }

            const widgetStatusForGa = computed(() => (isShowList.value ? 'JARVIS_Widget_Btn_Close' : 'JARVIS_Widget_Btn_Open'));

            function giveUpWaitingForWidgetResponse() {
                Object.keys(widgetDisplayState.value).forEach((name: string) => {
                    const widgetName = name as keyof typeof widgetDisplayState.value;
                    if (!widgetDisplayState.value[widgetName]) {
                        widgetDisplayState.value[widgetName] = false;
                    }
                });
            }

            function isShowMask(isDisplayMask: boolean) {
                isMaskEnabled.value = isDisplayMask;

                if (isDisplayMask) {
                    disableScrolling();
                } else {
                    enableScrolling();
                }

                useRootStore().setIsWidgetOpened(isDisplayMask);
                useHeaderDisplayStore().setState({ isShowSportsFooter: !isDisplayMask });
            }

            watch(() => isFunkyWidgetEnabled.value, () => {
                if (isFunkyWidgetEnabled.value && isAsi.value) {
                    widgetDisplayState.value.funky = null;

                    if (isLoadFunkyWidgetFromGames.value) {
                        const miniGameUrl = getMiniGameWidgetUrl();
                        const widgetUrl = `${miniGameUrl}/js/vue/funkyWidget.js?v=${inJoiVersion.value}`;
                        loadScript(widgetUrl);
                        const isNeedLoadProductionBuildFiles = !__ENV_STAGING__;
                        if (isNeedLoadProductionBuildFiles) {
                            const widgetCssUrl = `${miniGameUrl}/js/vue/funkyWidget.css?v=${inJoiVersion.value}`;
                            const commonJsUrl = `${miniGameUrl}/js/vue/chunk-common.js?v=${inJoiVersion.value}`;
                            const commonCssUrl = `${miniGameUrl}/js/vue/chunk-common.css?v=${inJoiVersion.value}`;
                            const vendorJsUrl = `${miniGameUrl}/js/vue/chunk-vendors.js?v=${inJoiVersion.value}`;

                            loadCss(widgetCssUrl);
                            loadScript(commonJsUrl);
                            loadCss(commonCssUrl);
                            loadScript(vendorJsUrl);
                        }
                    } else {
                        const funkyUrl = getFunkyWidgetUrl();
                        const widgetUrl = `${funkyUrl}/footballstrikemini/bundle.js?${new Date().getTime()}`;
                        loadScript(widgetUrl);
                    }
                } else {
                    widgetDisplayState.value.funky = null;
                }
            }, { immediate: true });

            watch(() => isLoadMiniGameWidget.value, () => {
                if (isLoadMiniGameWidget.value) {
                    if (isLoadMiniGameWidget.value) {
                        widgetDisplayState.value.miniGame = null;
                        const miniGameUrl = getMiniGameWidgetUrl();
                        const widgetUrl = `${miniGameUrl}/js/vue/miniGameWidget.js?v=${inJoiVersion.value}`;
                        loadScript(widgetUrl);
                        if (!__ENV_STAGING__) {
                            const widgetCssUrl = `${miniGameUrl}/js/vue/miniGameWidget.css?v=${inJoiVersion.value}`;
                            const commonJsUrl = `${miniGameUrl}/js/vue/chunk-common.js?v=${inJoiVersion.value}`;
                            const commonCssUrl = `${miniGameUrl}/js/vue/chunk-common.css?v=${inJoiVersion.value}`;
                            const vendorJsUrl = `${miniGameUrl}/js/vue/chunk-vendors.js?v=${inJoiVersion.value}`;

                            loadCss(widgetCssUrl);
                            loadScript(commonJsUrl);
                            loadCss(commonCssUrl);
                            loadScript(vendorJsUrl);
                        }
                    } else {
                        widgetDisplayState.value.miniGame = null;
                    }
                }
            }, { immediate: true });

            window.eventEmitter.on('sports.setWidgetDisplay', setWidgetDisplayState);
            const giveUpWaitingDuration = __GIVE_UP_WAITING_WIDGET_RESPONSE_TIME__;
            setTimeout(giveUpWaitingForWidgetResponse, giveUpWaitingDuration);

            onUnmounted(() => {
                enableScrolling();
                window.eventEmitter.off('sports.setWidgetDisplay', setWidgetDisplayState);
            });

            onClickOutside(widgetSwitchRef, () => {
                hideShowList();
            });

            return {
                PlayerType,
                isShowJackpotWidget,
                isShowInjoiDefaultWidgetEnabled,
                isShowList,
                zIndexMapping,
                ZIndex,
                widgetStatusForGa,
                miniGameImage,
                widgetDisplayCount,
                isShowMask,
                showWidgetList,
                customerInfo,
                widgetDisplayState,
                brandName,
                isShowWidgetSwitch,
                hideShowList,
                isMaskEnabled,
                isMiniGameWidgetEnabled,
                isFunkyWidgetEnabled,
                i18n,
                widgetSwitchRef,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/widgetSwitch";
</style>
